import { END_POINTS } from '@config';
import { RequestMethods } from '@constants/apiTypes';
import { api } from '@core/baseApi';
import {
  HTTPResponse,
  Scalars,
  SubmitAnswerRequest,
  SubmitQuestionFlagRequest
} from '@types';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllHomeWorks: builder.query<
      HTTPResponse<any>,
      { param: Scalars['QueryString'] }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.HOMEWORK_LIST}${reqBody.param}`,
        method: RequestMethods.GET
      })
    }),
    getHomeWorkDetails: builder.query<HTTPResponse<any>, Scalars['String']>({
      query: (assignmentId) => ({
        url: END_POINTS.HOMEWORKS.HOMEWORK_DETAILS(assignmentId),
        method: RequestMethods.GET
      })
    }),
    startHomeWork: builder.query<HTTPResponse<any>, Scalars['String']>({
      query: (assignmentId) => ({
        url: END_POINTS.HOMEWORKS.START_HOMEWORK(assignmentId),
        method: RequestMethods.POST
      })
    }),
    getHomeWorkSummary: builder.query<HTTPResponse<any>, Scalars['String']>({
      query: (assignmentId) => ({
        url: END_POINTS.HOMEWORKS.HOMEWORK_SUMMARY(assignmentId),
        method: RequestMethods.GET
      })
    }),
    getQuestionMapping: builder.query<HTTPResponse<any>, Scalars['String']>({
      query: (orgId) => ({
        url: END_POINTS.HOMEWORKS.GET_QUESTION_MAPPING(orgId),
        method: RequestMethods.GET
      })
    }),
    getQuestionDetails: builder.query<
      HTTPResponse<any>,
      {
        params: {
          assignmentId: string;
          questionId: string;
        };
      }
    >({
      query: (req) => ({
        url: END_POINTS.HOMEWORKS.GET_QUESTION_DETAILS(req.params),
        method: RequestMethods.GET
      })
    }),
    getFileUploadLink: builder.mutation<HTTPResponse<any>, { body: any }>({
      query: (req) => ({
        url: END_POINTS.HOMEWORKS.GET_FILE_UPLOAD_LINK,
        method: RequestMethods.POST,
        body: req.body
      })
    }),
    submitAnswer: builder.mutation<HTTPResponse<any>, SubmitAnswerRequest>({
      query: (credentials) => ({
        url: END_POINTS.HOMEWORKS.SUBMIT_ANSWER(
          credentials.param.homeworkId,
          credentials.param.questionId
        ),
        method: RequestMethods.POST,
        body: credentials.body
      })
    }),
    getOverallPerformance: builder.query<
      HTTPResponse<any>,
      { assignmentId: string }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.OVERALL_PERFORMANCE(
          reqBody.assignmentId
        )}`,
        method: RequestMethods.GET
      })
    }),
    getHomeWorkSummaryByStudentId: builder.query<
      HTTPResponse<any>,
      { assignmentId: Scalars['String']; studentId: Scalars['String'] }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: END_POINTS.HOMEWORKS.HOMEWORK_SUMMARY_BY_STUDENT_ID(
          assignmentId,
          studentId
        ),
        method: RequestMethods.GET
      })
    }),
    getOverallPerformanceByStudentId: builder.query<
      HTTPResponse<any>,
      { assignmentId: Scalars['String']; studentId: Scalars['String'] }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: `${END_POINTS.HOMEWORKS.OVERALL_PERFORMANCE_BY_STUDENT_ID(
          assignmentId,
          studentId
        )}`,
        method: RequestMethods.GET
      })
    }),
    getHomeWorkDetailsByStudentId: builder.query<
      HTTPResponse<any>,
      { assignmentId: Scalars['String']; studentId: Scalars['String'] }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: END_POINTS.HOMEWORKS.HOMEWORK_DETAILS_BY_STUDENT_ID(
          assignmentId,
          studentId
        ),
        method: RequestMethods.GET
      })
    }),
    getQuestionDetailsByStudentId: builder.query<
      HTTPResponse<any>,
      {
        assignmentId: string;
        questionId: string;
        studentId: string;
      }
    >({
      query: ({ assignmentId, questionId, studentId }) => ({
        url: END_POINTS.HOMEWORKS.GET_QUESTION_DETAILS_BY_STUDENT_ID({
          assignmentId,
          questionId,
          studentId
        }),
        method: RequestMethods.GET
      })
    }),
    flagQuestion: builder.mutation<
      HTTPResponse<any>,
      SubmitQuestionFlagRequest
    >({
      query: (credentials) => ({
        url: END_POINTS.HOMEWORKS.FLAG_QUESTION(credentials.param.questionId),
        method: RequestMethods.POST,
        body: credentials.body
      })
    }),
    getQuestionPreview: builder.query<
      HTTPResponse<any>,
      {
        questionId: string;
        previewToken: string;
      }
    >({
      query: ({ questionId, previewToken }) => ({
        url: END_POINTS.QUESTION.QUESTION_PREVIEW({ questionId }),
        method: RequestMethods.GET,
        headers: {
          Authorization: `Bearer ${previewToken}`
        }
      }),
      extraOptions: {
        skipAuthRedirect: true
      }
    }),
    getStudentSupplementaryActivity: builder.query<
      HTTPResponse<any>,
      { studentId: string; param: Scalars['QueryString'] }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_SUPPLIMENTARY_ACTIVITY(
          reqBody.studentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    }),
    getStudentTopicSkillPerformance: builder.query<
      HTTPResponse<any>,
      { studentId: string; param: Scalars['QueryString'] }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_TOPIC_SKILL_PERFORMANCE(
          reqBody.studentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    }),
    getStudentOverview: builder.query<
      HTTPResponse<any>,
      { studentId: string; param: Scalars['QueryString'] }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_OVERVIEW(
          reqBody.studentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    }),
    getStudentStrengthWeakness: builder.query<
      HTTPResponse<any>,
      { studentId: string; param: Scalars['QueryString'] }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_STRENGTH_WEAKNESS(
          reqBody.studentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    }),
    getStudentHomeWorkTopicSkillPerformance: builder.query<
      HTTPResponse<any>,
      any
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_HOMEWORK_TOPIC_SKILL_PERFORMANCE(
          reqBody.assignmentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    }),
    getStudentHomeWorkSupplementary: builder.query<HTTPResponse<any>, any>({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_HOMEWORK_SUPPLIMENTARY_ACTIVITY(
          reqBody.assignmentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    }),
    getStudentHomeWorkOverview: builder.query<HTTPResponse<any>, any>({
      query: (reqBody) => ({
        url: `${END_POINTS.HOMEWORKS.REPORTS_STUDENT_HOMEWORK_OVERVIEW(
          reqBody.assignmentId
        )}${reqBody.param}`,
        method: 'GET'
      })
    })
  })
});
// eslint-disable-next-line import/prefer-default-export
export const {
  useLazyGetAllHomeWorksQuery,
  useGetHomeWorkDetailsQuery,
  useLazyGetHomeWorkDetailsQuery,
  useLazyStartHomeWorkQuery,
  useLazyGetQuestionMappingQuery,
  useLazyGetHomeWorkSummaryQuery,
  useGetHomeWorkSummaryQuery,
  useGetQuestionMappingQuery,
  useGetQuestionDetailsQuery,
  useLazyGetQuestionDetailsQuery,
  useGetFileUploadLinkMutation,
  useSubmitAnswerMutation,
  useLazyGetOverallPerformanceQuery,
  useLazyGetHomeWorkSummaryByStudentIdQuery,
  useLazyGetOverallPerformanceByStudentIdQuery,
  useLazyGetHomeWorkDetailsByStudentIdQuery,
  useLazyGetQuestionDetailsByStudentIdQuery,
  useFlagQuestionMutation,
  useLazyGetQuestionPreviewQuery,
  useLazyGetStudentSupplementaryActivityQuery,
  useLazyGetStudentOverviewQuery,
  useLazyGetStudentTopicSkillPerformanceQuery,
  useLazyGetStudentStrengthWeaknessQuery,
  useLazyGetStudentHomeWorkOverviewQuery,
  useLazyGetStudentHomeWorkSupplementaryQuery,
  useLazyGetStudentHomeWorkTopicSkillPerformanceQuery
} = extendedBaseQuery;
